import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ENV } from '@shared/config/env'
import { ROUTES } from '@shared/config/routes'
import { getProfile } from '@entities/user'
import { useAppStore } from '@/store/useAppStore'
import { useAuthFlowStore } from '@features/web-auth'
import { setUnauthorizedHandler } from '@shared/api/baseApi'
import type { AxiosError } from 'axios'

export type BootstrapStatus = 'pending' | 'authenticated' | 'unauthenticated'

const PUBLIC_ROUTES: readonly string[] = [ROUTES.AUTH, ROUTES.TERMS, ROUTES.PRIVACY, ROUTES.WC]

const isPublicPath = (pathname: string) =>
  PUBLIC_ROUTES.some((r) => pathname === r || pathname.startsWith(r + '/'))

/**
 * В web-сборке: GET /user/profile при старте → 401 = redirect на /auth с сохранением
 * целевого URL. 200 = сохраняем профиль в стор.
 *
 * В tg-сборке: auth идёт через старый useTelegramWebApp (init-data + /user/auth).
 * Здесь дополнительно запрашиваем /user/profile для получения расширенной модели
 * (email/telegram/subscription в новой форме).
 */
export const useWebAuthBootstrap = () => {
  const [status, setStatus] = useState<BootstrapStatus>('pending')
  const navigate = useNavigate()
  const location = useLocation()
  const setProfile = useAppStore((s) => s.setProfile)
  const setSubscription = useAppStore((s) => s.setSubscription)
  const setAuthStatus = useAppStore((s) => s.setAuthStatus)
  const clearAuth = useAppStore((s) => s.clearAuth)
  const startedRef = useRef(false)

  useEffect(() => {
    // 401-handler для любых запросов через baseApi
    setUnauthorizedHandler((_err: AxiosError) => {
      if (ENV.IS_WEB) {
        clearAuth()
        if (!isPublicPath(window.location.pathname)) {
          const redirect = window.location.pathname + window.location.search
          navigate(`${ROUTES.AUTH}?redirect=${encodeURIComponent(redirect)}`, { replace: true })
        }
      }
    })
    return () => setUnauthorizedHandler(null)
  }, [navigate, clearAuth])

  useEffect(() => {
    if (!ENV.IS_WEB) return
    if (startedRef.current) return
    startedRef.current = true

    // На публичных роутах можно не ждать — но пытаемся подтянуть профиль на фоне,
    // чтобы если сессия есть — сразу редирект в приложение.
    const isPublic = isPublicPath(location.pathname)

    getProfile()
      .then((profile) => {
        if (profile.name === '') {
          // Не перетираем step, если флоу уже на блокирующем шаге
          // (persist сохранил его при reload — AuthFlow/ревалидация
          // разберутся сами). Иначе выставляем стартовый шаг по тому же
          // правилу, что handleVerify: нет TG → offer-telegram, иначе name.
          const flowStep = useAuthFlowStore.getState().step
          const flowInProgress =
            flowStep === 'code' || flowStep === 'offer-telegram' || flowStep === 'name'
          if (!flowInProgress) {
            useAuthFlowStore.getState().setStep(profile.telegram ? 'name' : 'offer-telegram')
          }
          setStatus('unauthenticated')
          setSubscription({ isActive: false })
          setAuthStatus('idle')
          if (!isPublic) {
            const redirect = location.pathname + location.search
            navigate(`${ROUTES.AUTH}?redirect=${encodeURIComponent(redirect)}`, { replace: true })
          }
          return
        }
        setProfile(profile)
        const sub = profile.subscription
        if (sub && sub.is_active) {
          setSubscription({
            isActive: true,
            expiresAt: sub.subscription_end_timestamp,
            subscriptionInfoId: sub.subscription_info_id,
            autoRenew: sub.auto_renew,
            provider: sub.provider,
            renewalPrice: sub.renewal_price,
            renewalCurrency: sub.renewal_currency
          })
        } else {
          setSubscription({ isActive: false })
        }
        setAuthStatus('success')
        setStatus('authenticated')
      })
      .catch(() => {
        setStatus('unauthenticated')
        setSubscription({ isActive: false })
        setAuthStatus('idle')
        if (!isPublic) {
          const redirect = location.pathname + location.search
          navigate(`${ROUTES.AUTH}?redirect=${encodeURIComponent(redirect)}`, { replace: true })
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { status }
}
